import React, { useRef } from "react";
import Styles from "./UpdateLedger.module.css";
import { Modal } from "@mui/material";
import { Close } from "@mui/icons-material";
import { useDispatch, useSelector } from "react-redux";
import {
  fetchDocumentUrlByLedgerId,
  fetchLedgerOngoing,
  fetchMatchmakingResults,
  fetchTermsDoc,
  fetchAllBankingListings,
} from "../../../../Redux/Slices/bankingSlice";
import { useState, useEffect, useMemo } from "react";
import TextInput from "../../../../commons/Inputs/TextInput/TextInput";
import { ReactComponent as EditIcon } from "../../../../assets/images/Edit.svg";
import { ReactComponent as SquareIcon } from "../../../../assets/images/squareIcon.svg";
import {
  acceptRejectLedger,
  createLedger,
  updateLedger,
  uploadLedgerFile,
} from "../../../../Services/ApiCalling/banking";
import { toast } from "react-toastify";
import { ReactComponent as Upload } from "../../../../assets/images/fileUpload.svg";
import EditModal from "./EditModal/EditModal";
import { ReactComponent as Right } from "../../../../assets/images/right.svg";
import { ReactComponent as Cross } from "../../../../assets/images/cross.svg";
import PreviewModal from "../../../../commons/PreviewModal/PreviewModal";

const UpdateLedger = ({
  open,
  handleClose,
  connectionData,
  ongoingData,
  activeListingId,
  currentPage,
  itemsPerPage,
  totalCount,
  onPageChange,
  onLedgerUpdated,
  listingId,
  connectionId,
}) => {
  const dispatch = useDispatch();
  const [fileValue, setFileValue] = useState(null);
  const [selectedFileData, setSelectedFileData] = useState(null);
  const [injection, setInjection] = useState("");
  const [drawal, setDrawal] = useState("");
  const [month, setMonth] = useState("");
  const [loadingDocId, setLoadingDocId] = useState(null);
  const [editModal, setEditModal] = useState(false);
  const [selectedLedger, setSelectedLedger] = useState(null);
  const [loadedFilenames, setLoadedFilenames] = useState({});
  const [previewModal, setPreviewModal] = useState(false);
  const [previewFileUrl, setPreviewFileUrl] = useState(null);
  const [selectedDocumentId, setSelectedDocumentId] = useState(null);
  const [applyLoading, setApplyLoading] = useState(false);

  const userData = useSelector((state) => state.persistedReducer);

  const ledgerList = useMemo(
    () => connectionData?.ledgers || [],
    [connectionData?.ledgers]
  );

  const fetchingFilenames = useRef(false);
  const loadedFilenamesRef = useRef({});

  useEffect(() => {
    const fetchMissingFilenames = async () => {
      // Prevent multiple simultaneous fetches
      if (fetchingFilenames.current) {
        return;
      }

      const ledgersNeedingFilenames = ledgerList.filter(
        (ledger) =>
          ledger.ledgerDocumentId &&
          !ledger.ledgerDocumentName &&
          !ledger.filename &&
          !loadedFilenamesRef.current[ledger.ledgerDocumentId]
      );

      if (ledgersNeedingFilenames.length === 0) {
        return;
      }

      fetchingFilenames.current = true;

      try {
        for (const ledger of ledgersNeedingFilenames) {
          try {
            const resultAction = await dispatch(
              fetchTermsDoc(ledger.ledgerDocumentId)
            );
            if (fetchTermsDoc.fulfilled.match(resultAction)) {
              const { fileName } = resultAction.payload;

              // Update both state and ref
              loadedFilenamesRef.current[ledger.ledgerDocumentId] = fileName;
              setLoadedFilenames((prev) => ({
                ...prev,
                [ledger.ledgerDocumentId]: fileName,
              }));
            }
          } catch (error) {
            console.error("Error fetching document filename:", error);
            loadedFilenamesRef.current[ledger.ledgerDocumentId] = "View File";
            setLoadedFilenames((prev) => ({
              ...prev,
              [ledger.ledgerDocumentId]: "View File",
            }));
          }
        }
      } finally {
        fetchingFilenames.current = false;
      }
    };

    if (ledgerList.length > 0) {
      fetchMissingFilenames();
    }
  }, [ledgerList, dispatch]);

  useEffect(() => {
    let isMounted = true;

    if (open) {
      // Reset form state when modal opens
      resetFormState();

      const refreshData = async () => {
        try {
          const promises = [
            dispatch(fetchLedgerOngoing({ currentPage, size: itemsPerPage })),
          ];

          if (activeListingId) {
            promises.push(
              dispatch(
                fetchMatchmakingResults({
                  id: activeListingId,
                  statusFilter: null,
                })
              )
            );
          }

          promises.push(dispatch(fetchAllBankingListings()));

          await Promise.all(promises);
        } catch (error) {
          if (isMounted) {
            console.error("Error refreshing data when modal opens:", error);
          }
        }
      };

      refreshData();
    }

    return () => {
      isMounted = false;
    };
  }, [open]);

  const formatMonth = (value) => {
    if (!value) return "";
    const [year, month] = value.split("-");
    const date = new Date(`${year}-${month}-01`);
    const monthStr = date
      .toLocaleString("default", { month: "short" })
      .slice(0, 3);
    return `${monthStr}-${year}`;
  };

  const handleApplySubmit = async () => {
    const hasExistingFile = fileValue?.id;
    const hasNewFile = selectedFileData;
    if (
      (!hasExistingFile && !hasNewFile) ||
      injection === "" ||
      drawal === "" ||
      !month
    ) {
      console.error("Missing required fields");
      toast.error("Please fill all required fields");
      return;
    }

    if (applyLoading) {
      return;
    }

    setApplyLoading(true);
    let ledgerDocumentId = null;

    try {
      if (selectedFileData) {
        const formData = new FormData();
        formData.append("file", selectedFileData);

        const response = await uploadLedgerFile(
          formData,
          connectionId || connectionData?.connectionId,
          listingId
        );

        const fileId = response?.data?.id ?? response?.data?.data?.id;
        const fileName =
          response?.data?.filename ??
          response?.data?.data?.filename ??
          selectedFileData?.name;

        if (!fileId) {
          throw new Error("File ID not found in upload response");
        }

        ledgerDocumentId = fileId;

        setFileValue({
          id: fileId,
          fileName: fileName,
        });
        setSelectedFileData(null);
      } else {
        ledgerDocumentId = fileValue?.id;
      }

      if (!ledgerDocumentId) {
        throw new Error("No valid document ID available");
      }

      const payload = {
        connectionId: connectionData?.connectionId,
        month: formatMonth(month),
        amount: Number(injection),
        againstAmount: Number(drawal),
        ledgerDocumentId: ledgerDocumentId,
      };

      if (selectedLedger?.id) {
        payload.id = selectedLedger.id;
      }

      let result;
      if (selectedLedger?.id) {
        result = await updateLedger(payload);
      } else {
        result = await createLedger(payload);
      }

      await dispatch(
        fetchLedgerOngoing({ currentPage, size: itemsPerPage })
      ).unwrap();

      if (activeListingId) {
        await dispatch(
          fetchMatchmakingResults({
            id: activeListingId,
            statusFilter: null,
          })
        );
      }

      await dispatch(fetchAllBankingListings());

      if (onLedgerUpdated) {
        onLedgerUpdated();
      }

      resetFormState();
      toast.success(
        selectedLedger
          ? "Ledger updated successfully"
          : "Ledger created successfully"
      );
      if (editModal) {
        setEditModal(false);
        handleModalClose();
      } else {
        handleModalClose();
      }
    } catch (error) {
      console.error("Error saving ledger:", error);
      console.error("Error details:", error.response?.data || error.message);

      if (error.message && error.message.includes("upload")) {
        toast.error("File upload failed. Please try again.");
      } else if (error.message && error.message.includes("document ID")) {
        toast.error("File error. Please select a file and try again.");
      } else {
        toast.error(
          error.response?.data?.message ||
            "Failed to save ledger. Please try again."
        );
      }
    } finally {
      setApplyLoading(false);
    }
  };

  const handleFileChange = (selectedFileData) => {
    if (!selectedFileData?.fileName && !selectedFileData?.name) return;

    setSelectedFileData(selectedFileData);

    setFileValue(null);
  };

  const handleLedgerAction = async (ledgerId, isApproving) => {
    try {
      await acceptRejectLedger(ledgerId, isApproving);

      toast.success(isApproving ? "Ledger approved" : "Ledger rejected");

      await Promise.all(
        [
          dispatch(
            fetchLedgerOngoing({ currentPage, size: itemsPerPage })
          ).unwrap(),

          activeListingId &&
            dispatch(
              fetchMatchmakingResults({
                id: activeListingId,
                statusFilter: null,
              })
            ),

          dispatch(fetchAllBankingListings()),
        ].filter(Boolean)
      );

      if (onLedgerUpdated) {
        onLedgerUpdated();
      }

      handleModalClose();
    } catch (error) {
      console.error("Error in ledger approval/rejection:", error);
      toast.error("Action failed. Please try again.");
    }
  };

  const handleViewFile = async (ledgerDocumentId) => {
    setLoadingDocId(ledgerDocumentId);
    try {
      const resultAction = await dispatch(
        fetchDocumentUrlByLedgerId(ledgerDocumentId)
      );
      if (fetchDocumentUrlByLedgerId.fulfilled.match(resultAction)) {
        const url = resultAction.payload?.url || resultAction.payload;
        setPreviewFileUrl(url);
        setSelectedDocumentId(ledgerDocumentId);
        setPreviewModal(true);
      } else {
        console.error("Error fetching document URL");
        toast.error("Failed to fetch document");
      }
    } catch (error) {
      console.error("Error fetching document:", error);
      toast.error("Failed to fetch document");
    } finally {
      setLoadingDocId(null);
    }
  };

  const handleOpenEdit = async (ledgerId) => {
    const ledgerObj = ledgerList.find((ledger) => ledger.id === ledgerId);

    if (!ledgerObj) {
      console.error("Ledger not found");
      return;
    }

    setSelectedLedger(ledgerObj);

    if (ledgerObj.month) {
      const [monStr, year] = ledgerObj.month.split("-");
      const monthVal = new Date(`${monStr} 01 ${year}`).getMonth() + 1;
      setMonth(`${year}-${String(monthVal).padStart(2, "0")}`);
    } else {
      setMonth("");
    }

    setInjection(ledgerObj.amount ? String(ledgerObj.amount) : "");
    setDrawal(ledgerObj.againstAmount ? String(ledgerObj.againstAmount) : "");

    // Handle file - check if already loaded first
    if (ledgerObj.ledgerDocumentName) {
      setFileValue({
        id: ledgerObj.ledgerDocumentId,
        fileName: ledgerObj.ledgerDocumentName,
      });
      setSelectedFileData(null);
      setEditModal(true);
    } else if (ledgerObj.ledgerDocumentId) {
      // Check if already in loadedFilenames or ref
      const cachedFilename =
        loadedFilenames[ledgerObj.ledgerDocumentId] ||
        loadedFilenamesRef.current[ledgerObj.ledgerDocumentId];

      if (cachedFilename) {
        setFileValue({
          id: ledgerObj.ledgerDocumentId,
          fileName: cachedFilename,
        });
        setSelectedFileData(null);
        setEditModal(true);
      } else {
        // Only fetch if not cached
        try {
          const resultAction = await dispatch(
            fetchTermsDoc(ledgerObj.ledgerDocumentId)
          );
          if (fetchTermsDoc.fulfilled.match(resultAction)) {
            const { fileName } = resultAction.payload;

            // Update cache
            loadedFilenamesRef.current[ledgerObj.ledgerDocumentId] = fileName;
            setLoadedFilenames((prev) => ({
              ...prev,
              [ledgerObj.ledgerDocumentId]: fileName,
            }));

            setFileValue({
              id: ledgerObj.ledgerDocumentId,
              fileName: fileName,
            });
          } else {
            setFileValue({
              id: ledgerObj.ledgerDocumentId,
              fileName: `Document_${ledgerObj.ledgerDocumentId}`,
            });
          }
        } catch (error) {
          console.error("Error fetching document filename:", error);
          setFileValue({
            id: ledgerObj.ledgerDocumentId,
            fileName: `Document_${ledgerObj.ledgerDocumentId}`,
          });
        }
        setSelectedFileData(null);
        setEditModal(true);
      }
    } else {
      setFileValue(null);
      setSelectedFileData(null);
      setEditModal(true);
    }
  };

  const resetFormState = () => {
    setFileValue(null);
    setSelectedFileData(null);
    setInjection("");
    setDrawal("");
    setMonth("");
    setSelectedLedger(null);
    // Don't reset editModal, previewModal here as they have their own close handlers
  };

  const handleModalClose = () => {
    resetFormState();
    handleClose();
  };

  const handleCloseEdit = () => {
    setEditModal(false);
  };

  const handleClosePreview = () => {
    setPreviewModal(false);
    setPreviewFileUrl(null);
    setSelectedDocumentId(null);
  };

  const truncateFileName = (fileName, maxLength = 25) => {
    if (!fileName) return "";
    if (fileName.length <= maxLength) return fileName;

    const extension = fileName.split(".").pop();
    const nameWithoutExt = fileName.substring(0, fileName.lastIndexOf("."));
    const truncatedName = nameWithoutExt.substring(
      0,
      maxLength - extension.length - 4
    );

    return `${truncatedName}...${extension}`;
  };

  return (
    <>
      {editModal && (
        <EditModal
          open={editModal}
          handleClose={handleCloseEdit}
          ledgerId={selectedLedger?.id}
          month={month}
          setMonth={setMonth}
          injection={injection}
          setInjection={setInjection}
          drawal={drawal}
          setDrawal={setDrawal}
          fileValue={fileValue}
          selectedFileData={selectedFileData}
          handleFileChange={handleFileChange}
          handleApplySubmit={handleApplySubmit}
          applyLoading={applyLoading}
        />
      )}
      <Modal
        open={open}
        onClose={handleModalClose}
        aria-labelledby="child-modal-title"
        aria-describedby="child-modal-description"
      >
        <div className={`popupModal ${Styles.createEditModalCont}`}>
          <div
            className={`display_flex_row modalHeading ${Styles.modalHeading}`}
          >
            <div className="headingText">Update Ledger</div>
            <Close
              style={{ fontSize: "24px", color: "#777777", cursor: "pointer" }}
              onClick={handleModalClose}
            />
          </div>
          <div className={Styles.upMainCont}>
            <div className={Styles.addLedgerSection}>
              <div className={Styles.upFirstCont}>
                <div>Add Record</div>
              </div>
              <div className={Styles.addSectionLedger2}>
                <div className={Styles.formRow}>
                  <div className={Styles.fieldGroup}>
                    <label className={Styles.fieldLabel}>Month*</label>
                    <input
                      type="month"
                      value={month}
                      max={new Date().toISOString().slice(0, 7)}
                      onChange={(e) => setMonth(e.target.value)}
                      className={Styles.monthInput}
                    />
                  </div>

                  <div className={Styles.fieldGroup}>
                    {/* <label className={Styles.fieldLabel}>Injection (MW)</label> */}
                    <TextInput
                      example="Enter injection"
                      heading={"Injection (MW) *"}
                      value={injection}
                      onChange={(val) => {
                        if (/^\d*$/.test(val)) {
                          setInjection(val);
                        }
                      }}
                      type="number"
                      min={0}
                      height={35}
                    />
                  </div>

                  <div className={Styles.fieldGroup}>
                    {/* <label className={Styles.fieldLabel}>Drawal (MW)</label> */}
                    <TextInput
                      example="Enter drawal"
                      heading={"Drawal (MW) *"}
                      value={drawal}
                      onChange={(val) => {
                        if (/^\d*$/.test(val)) {
                          setDrawal(val);
                        }
                      }}
                      type="number"
                      min={0}
                      height={35}
                    />
                  </div>

                  <div className={Styles.fieldGroup}>
                    <label className={Styles.fieldLabel}>
                      Regional Energy Account *
                    </label>
                    <div className={Styles.customFileUpload}>
                      <label
                        htmlFor="ledgerFileUpload"
                        className={Styles.uploadLabel}
                      >
                        {fileValue?.fileName ? (
                          <span
                            className={Styles.uploadedFileName}
                            title={fileValue.fileName}
                          >
                            <strong>
                              {truncateFileName(fileValue.fileName)}
                            </strong>
                          </span>
                        ) : selectedFileData?.name ? (
                          <span
                            className={Styles.uploadedFileName}
                            title={selectedFileData.name}
                          >
                            <strong>
                              {truncateFileName(selectedFileData.name)}
                            </strong>
                          </span>
                        ) : (
                          <div className={Styles.uploadHeader}>
                            <div> Upload Ledger PDF</div>
                            <span>
                              <Upload
                                width="28px"
                                height="28px"
                                display="flex"
                                justifyContent="right"
                              />
                            </span>
                          </div>
                        )}
                      </label>
                      <input
                        type="file"
                        id="ledgerFileUpload"
                        accept=".pdf"
                        onChange={(e) => handleFileChange(e.target.files[0])}
                        style={{ display: "none" }}
                      />

                      {fileValue?.error && (
                        <div className={Styles.errorMessage}>
                          {fileValue.error}
                        </div>
                      )}
                    </div>
                  </div>

                  <div className={Styles.fieldGroup}>
                    <label className={Styles.fieldLabel}></label>
                    <button
                      onClick={handleApplySubmit}
                      disabled={
                        applyLoading ||
                        !month ||
                        (!fileValue?.id && !selectedFileData) ||
                        injection === "" ||
                        drawal === ""
                      }
                      className={`${Styles.applyButton} ${
                        applyLoading ||
                        !month ||
                        (!fileValue?.id && !selectedFileData) ||
                        injection === "" ||
                        drawal === ""
                          ? Styles.applyButtonDisabled
                          : ""
                      }`}
                    >
                      {applyLoading ? "Applying..." : "Apply"}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
          {ledgerList.length > 0 && (
            <div
              className={`display_flex_row_center ${Styles.updateOtherSection}`}
            >
              <div className={`${Styles.updateSectionTable}`}>
                <div className={`display_flex_row ${Styles.ledgerTableHeader}`}>
                  <div>Month</div>
                  <div>Injection</div>
                  <div>Drawal</div>
                  <div>File</div>
                  <div>Edit</div>
                </div>
                <div className={`scroll_bar_y  ${Styles.ledgerTableContent}`}>
                  {ledgerList.map((ledger) => {
                    const currentUserId = userData?.user?.user?.userId;
                    const ledgerInitiatorId = ledger?.initiatorId;
                    const ledgerApproverId = ledger?.approverId;

                    const isApprovalStatusInitiated =
                      ledger?.approvalStatus === "INITIATED";

                    const isCounterParty = currentUserId !== ledgerInitiatorId;

                    const isUserEligibleToApprove =
                      !ledgerApproverId && isCounterParty;

                    const shouldShowApprovalButtons =
                      isApprovalStatusInitiated && isUserEligibleToApprove;

                    // Swap injection and drawal values for counterparty
                    const displayInjection = isCounterParty
                      ? ledger.againstAmount
                      : ledger.amount;
                    const displayDrawal = isCounterParty
                      ? ledger.amount
                      : ledger.againstAmount;

                    return (
                      <div
                        key={ledger.id}
                        className={`display_flex_row ${Styles.ledgerTableCon}`}
                      >
                        <div>{ledger.month}</div>
                        <div>{displayInjection} MW</div>
                        <div>{displayDrawal} MW</div>
                        <div>
                          {loadingDocId === ledger?.ledgerDocumentId ? (
                            <span>Loading...</span>
                          ) : (
                            <div
                              style={{
                                width: "100%",
                                display: "flex",
                                justifyContent: "center",
                                alignItems: "center",
                                gap: "6px",
                              }}
                            >
                              <span>View File</span>
                              <span
                                onClick={() =>
                                  handleViewFile(ledger?.ledgerDocumentId)
                                }
                                style={{
                                  color: "blue",
                                  textDecoration: "underline",
                                  cursor: "pointer",
                                }}
                              >
                                <SquareIcon />
                              </span>
                            </div>
                          )}
                        </div>
                        <div>
                          <div className={Styles.actionButtons}>
                            {ledger?.approvalStatus === "APPROVED" ? (
                              <div className={Styles.approveMain}>
                                <div className={Styles.approvedStatus}>
                                  <span>File Approved</span>
                                </div>
                              </div>
                            ) : ledger?.approvalStatus === "REJECTED" ? (
                              <div className={Styles.rejectedWrapper}>
                                <span className={Styles.rejectedStatus}>
                                  Rejected
                                </span>
                              </div>
                            ) : shouldShowApprovalButtons ? (
                              <div className={Styles.btnsSection}>
                                <button
                                  className={Styles.rejectBtn}
                                  onClick={() =>
                                    handleLedgerAction(ledger.id, 0)
                                  }
                                >
                                  <span>
                                    <Cross style={{ paddingBottom: "2px" }} />
                                  </span>
                                  Reject
                                </button>
                                <button
                                  className={Styles.approveBtn}
                                  onClick={() =>
                                    handleLedgerAction(ledger.id, 1)
                                  }
                                >
                                  <span>
                                    <Right />
                                  </span>
                                  Approve
                                </button>
                              </div>
                            ) : (
                              <div
                                className={Styles.editIcon}
                                onClick={() => handleOpenEdit(ledger?.id)}
                              >
                                <span>
                                  <EditIcon />
                                </span>
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          )}
        </div>
      </Modal>

      <PreviewModal
        open={previewModal}
        onClose={handleClosePreview}
        fileUrl={previewFileUrl}
        documentId={selectedDocumentId}
      />
    </>
  );
};

export default UpdateLedger;
